import React, { useState } from 'react';
import { FaClipboardList, FaCheckCircle, FaLightbulb } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CreateChecklistImage from '../images/Create new checklist.jpg';
import ModifyChecklistImage from '../images/Modify your checklist.jpg';
import DeleteTaskImage from '../images/Delete a task.jpg';
import DownloadChecklistImage from '../images/Download Checklist.jpg';

const Feature = ({ icon, title, description }) => (
  <div className="flex flex-col items-center text-center p-10">
    {icon}
    <h3 className="mt-8 text-3xl font-semibold">{title}</h3>
    <p className="mt-6 text-xl">{description}</p>
  </div>
);
Feature.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

const ChecklistSection = ({ image, title, description, reverse }) => (
  <div className={`flex flex-col md:flex-row ${reverse ? 'md:flex-row-reverse' : ''} items-center mb-40`}>
    <img src={image} alt={title} className="w-full md:w-1/2 h-auto mb-12 md:mb-0 md:mx-20" />
    <div className="w-full md:w-1/2 px-12">
      <h2 className="text-4xl font-bold mb-8">{title}</h2>
      <p className="text-2xl">{description}</p>
    </div>
  </div>
);
ChecklistSection.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  reverse: PropTypes.bool,
};

export default function Checklistpage() {
  const [openQuestion, setOpenQuestion] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const toggleQuestion = (index) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      {/* Top section */}
      <div className="w-full max-w-8xl mx-auto px-12 py-24">
        <h1 className="text-6xl font-bold text-center mb-12">{t('Stay ahead of every to-do with the Hayya Checklist')}</h1>
        <p className="text-3xl text-center mb-24">
          {t("Tell us a little about your wedding and we'll create customizable tasks for every stage of your planning journey")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 mb-32">
          <Feature
            icon={<FaClipboardList className="text-7xl text-blue-500" />}
            title={t('Personalize your tasks')}
            description={t('Add, edit, or delete tasks at any time to easily customize your Hayya Checklist.')}
          />
          <Feature
            icon={<FaCheckCircle className="text-7xl text-green-500" />}
            title={t('Stay on track')}
            description={t('Make sure you complete your tasks at the right time leading up to your day.')}
          />
          <Feature
            icon={<FaLightbulb className="text-7xl text-yellow-500" />}
            title={t('Get recommendations')}
            description={t("Depending on traditions you'd like to incorporate or situations like postponing your wedding, get unique to-dos you may need to consider.")}
          />
        </div>

        <div className="text-center mb-32">
          <h2 className="text-5xl font-bold mb-10">{t("Don't miss a thing")}</h2>
          <p className="text-3xl">
            {t("We'll help make sure you have everything you need to do organized in a manageable and easily digestible format. That way, you won't get overwhelmed and you'll know what's coming next!")}
          </p>
        </div>
      </div>

      {/* Checklist sections */}
      <div className="w-full max-w-8xl mx-auto px-12 py-24">
        <ChecklistSection
          image={CreateChecklistImage}
          title={t('Create a New Checklist')}
          description={t('Craft a personalized wedding journey by creating a new checklist that reflects your unique vision and preferences.')}
        />
        <ChecklistSection
          image={ModifyChecklistImage}
          title={t('Modify Your Checklist')}
          description={t('Easily adapt your checklist to your evolving wedding plans by adding, removing, or adjusting tasks and deadlines as needed.')}
          reverse
        />
        <ChecklistSection
          image={DeleteTaskImage}
          title={t('Delete a Task')}
          description={t('Simplify your planning process by deleting tasks that are no longer relevant, allowing you to focus on what truly matters for your big day.')}
        />
        <ChecklistSection
          image={DownloadChecklistImage}
          title={t('Download Checklist')}
          description={t('Take your planning on the go by downloading your checklist as a PDF, ensuring you have access to your tasks wherever you are.')}
          reverse
        />
      </div>

      {/* FAQ section */}
      <div className="w-full max-w-4xl mx-auto space-y-16 px-12 py-24">
        <button
          className="w-full flex justify-center py-4 px-8 border border-transparent rounded-md shadow-sm text-xl font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
          onClick={() => navigate('/dashboard/todo-list')}
        >
          {t('Go to Checklist')}
        </button>
        <div className="mt-16 text-lg text-gray-600">
          <h2 className="text-3xl font-bold mb-8">{t('Frequently Asked Questions')}</h2>
          <p className="mb-10">{t("Have questions about the Checklist tool? We've got you.")}</p>
          <dl className="space-y-10">
            {[
              { q: t("Is your Wedding Checklist free?"), a: t("Yes, the Hayya Wedding Checklist is free (and easy) to use!") },
              { q: t("What do I need to put on my Wedding Checklist?"), a: t("We'll help you with the default Checklist items. Not planning on something traditional? No problem—add or edit away to customize to your own wedding day.") },
              { q: t("Can I plan my entire wedding with your Wedding Checklist?"), a: t("Yes! Our free Wedding Checklist helps make sure you know everything you need to do and works closely with our other planning tools so you can check everything off your list.") },
              { q: t("Does your Wedding Checklist include a timeline?"), a: t("Yes, the Hayya Wedding Checklist includes how many months in advance you should complete each task.") },
              { q: t("Can I print my Wedding Checklist?"), a: t("Yes! You can easily download or print your entire Wedding Checklist with the click of a button.") },
            ].map((item, index) => (
              <div key={index} className="border-b border-gray-200 pb-6">
                <dt
                  className="text-2xl font-bold cursor-pointer flex justify-between items-center"
                  onClick={() => toggleQuestion(index)}
                >
                  {item.q}
                  <span>{openQuestion === index ? '▲' : '▼'}</span>
                </dt>
                {openQuestion === index && <dd className="mt-6 text-xl">{item.a}</dd>}
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}